import { FC, useState, useEffect, useMemo, useCallback, memo } from "react";
import { useOrdersStore } from "@/pages/orders-new/use-orders-store";
import {
  User,
  MapPin,
  Clock,
  MessageSquare,
  Bike,
  Wifi,
  WifiOff,
  Phone,
} from "lucide-react";
import { getOrderStatusDisplay } from "@/lib/statusConfig";
import { calculateOrderTotal, formatCurrency } from "@/lib/formatCurrency";
import { useNavigate } from "react-router-dom";
import { useOrdersApi } from "@/hooks/useOrdersApi";
import { useOrderUpdates } from "@/hooks/useOrderUpdates";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/atoms/sheet";

interface OrderSheetProps {
  isOpen: boolean;
  onClose: () => void;
  orderIds: string[];
  realTime?: any;
  isTestMode?: boolean;
}

const OrderSheet: FC<OrderSheetProps> = ({
  isOpen,
  onClose,
  orderIds,
  isTestMode = false,
}) => {
  const { setOrderDetail } = useOrdersStore();
  const { fetchOrderDriversHistory } = useOrdersApi();
  const navigate = useNavigate();
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { fetchOrdersByIds } = useOrdersApi();

  // Fetch helper is provided by useOrdersApi now

  // Use the custom hook for real-time order updates
  const { getOrderWithUpdates, connectionStatus } = useOrderUpdates({
    orderIds,
    isEnabled: isOpen && !isTestMode,
    isTestMode,
  });

  // Memoize the orderIds array to prevent unnecessary re-renders
  const memoizedOrderIds = useMemo(() => {
    // Only update if the actual order IDs have changed
    return orderIds.sort().join(",");
  }, [orderIds]);

  // Load orders when orderIds change
  useEffect(() => {
    if (isOpen && orderIds.length > 0) {
      setLoading(true);
      setError(null);
      fetchOrdersByIds(orderIds)
        .then(async (list) => {
          // Fetch drivers history for each order (current first)
          const withHistory = await Promise.all(
            (list as any[]).map(async (o) => {
              try {
                const hist = await fetchOrderDriversHistory(Number(o.id));
                return {
                  ...o,
                  driversHistory: Array.isArray(hist) ? hist : [],
                };
              } catch {
                return { ...o, driversHistory: [] };
              }
            }),
          );
          setOrders(withHistory);
        })
        .catch(() => setError("Error al cargar las órdenes"))
        .finally(() => setLoading(false));
    } else {
      setOrders([]);
    }
    // Use memoizedOrderIds instead of orderIds to prevent unnecessary effects
  }, [isOpen, memoizedOrderIds]);

  // Memoized helper function to get status color
  const getStatusColor = useCallback((status: string) => {
    switch (status) {
      case "delivered":
        return "bg-green-100 text-green-800";
      case "in_transit":
        return "bg-blue-100 text-blue-800";
      case "pending":
        return "bg-yellow-100 text-yellow-800";
      case "cancelled":
        return "bg-red-100 text-red-800";
      case "completed":
        return "bg-green-100 text-green-800";
      case "driver_at_branch":
        return "bg-blue-100 text-blue-800";
      case "sorted":
        return "bg-orange-100 text-orange-800";
      case "accepted":
        return "bg-sky-100 text-sky-800";
      case "on_site":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  }, []);

  // Memoized toggle function
  const toggleOrderDetails = useCallback((orderId: string) => {
    setExpandedOrderId((prev) => (prev === orderId ? null : orderId));
  }, []);

  // Memoized navigation handler
  const handleNavigateToOrder = useCallback(
    (orderId: string) => {
      // Set the order as selected in the store
      setOrderDetail(parseInt(orderId));
      // Close the modal
      onClose();
      // Navigate to orders page
      navigate(`/orders/${orderId}`);
    },
    [setOrderDetail, onClose, navigate],
  );

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-lg">
        <SheetHeader>
          <div className="flex items-center gap-3">
            <SheetTitle className="text-lg font-medium text-gray-900">
              Órdenes
            </SheetTitle>
            {!isTestMode && (
              <div className="flex items-center gap-2">
                {connectionStatus === "connected" ? (
                  <div className="flex items-center gap-1 text-xs text-green-600">
                    <Wifi className="h-3 w-3" />
                    <span>En vivo</span>
                  </div>
                ) : connectionStatus === "connecting" ? (
                  <div className="flex items-center gap-1 text-xs text-yellow-600">
                    <div className="animate-spin h-3 w-3 border border-yellow-600 border-t-transparent rounded-full"></div>
                    <span>Conectando...</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-xs text-gray-400">
                    <WifiOff className="h-3 w-3" />
                    <span>Sin conexión</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </SheetHeader>

        {/* Content */}
        <div className="mt-6 space-y-3 overflow-y-auto max-h-[calc(100vh-120px)] pb-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-sm text-gray-600">
                Cargando órdenes...
              </span>
            </div>
          ) : error ? (
            <div className="text-center py-8">
              <div className="text-red-600 mb-2 text-sm">{error}</div>
            </div>
          ) : orders.length > 0 ? (
            <>
              {/* Orders List */}
              {orders.map((order) => {
                // Apply real-time updates to each order
                const updatedOrder = isTestMode
                  ? order
                  : getOrderWithUpdates(order);

                return (
                  <OrderItem
                    key={order.tracking_id || order.id}
                    order={updatedOrder}
                    isTestMode={isTestMode}
                    isExpanded={expandedOrderId === order.id}
                    onToggle={toggleOrderDetails}
                    onNavigateToOrder={handleNavigateToOrder}
                    getStatusColor={getStatusColor}
                  />
                );
              })}
            </>
          ) : (
            <div className="text-center py-8 text-sm text-gray-500">
              No se encontraron órdenes
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

// Memoized OrderItem component to prevent unnecessary re-renders
const OrderItem = memo(
  ({
    order,
    isTestMode,
    isExpanded,
    onToggle,
    onNavigateToOrder,
    getStatusColor,
  }: {
    order: any;
    isTestMode: boolean;
    isExpanded: boolean;
    onToggle: (orderId: string) => void;
    onNavigateToOrder: (orderId: string) => void;
    getStatusColor: (status: string) => string;
  }) => {
    const handleToggle = useCallback(() => {
      onToggle(order.id);
    }, [onToggle, order.id]);

    const handleNavigate = useCallback(() => {
      onNavigateToOrder(order.id);
    }, [onNavigateToOrder, order.id]);

    return (
      <div className="border rounded-lg overflow-hidden">
        {/* Order Header */}
        <div
          className="p-3 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
          onClick={handleToggle}
        >
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-900">
              #{isTestMode ? order.id : order.tracking_id || order.id}
            </span>
            <span className="text-xs text-gray-500">
              {isTestMode ? `Grupo ${order.group}` : `Orden ${order.id}`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span
              className={`text-xs px-2 py-1 rounded-full ${getStatusColor(order.status)}`}
            >
              {getOrderStatusDisplay(order.status).display}
            </span>
            <svg
              className={`w-4 h-4 text-gray-400 transition-transform ${
                isExpanded ? "rotate-180" : ""
              }`}
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>

        {/* Expanded Details */}
        {isExpanded && (
          <div className="border-t bg-white">
            {/* Detalles del pedido */}
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-900">
                  Detalles del pedido
                </h3>
                <button
                  type="button"
                  className="text-sm text-blue-600 font-medium hover:underline"
                  onClick={handleNavigate}
                >
                  Ver orden
                </button>
              </div>
              <div className="space-y-3">
                <div className="flex items-center">
                  <User className="h-4 w-4 text-gray-600 mr-2" />
                  <div className="flex-1 flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-800">
                      Cliente:
                    </span>
                    <span className="text-sm text-gray-800">
                      {isTestMode
                        ? order.customer.full_name
                        : order.customer?.full_name || "N/A"}
                    </span>
                  </div>
                </div>

                {/* Phone number */}
                <div className="flex items-center">
                  <Phone className="h-4 w-4 text-gray-600 mr-2" />
                  <div className="flex-1 flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-800">
                      Teléfono:
                    </span>
                    <span className="text-sm text-gray-800">
                      {isTestMode
                        ? order.customer.phone
                        : order.customer?.phone || "-"}
                    </span>
                  </div>
                </div>

                <div className="flex items-center">
                  <MapPin className="h-4 w-4 text-gray-600 mr-2" />
                  <div className="flex-1 flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-800">
                      Dirección de entrega:
                    </span>
                    <span className="text-sm text-gray-600 text-right max-w-[150px] truncate">
                      {isTestMode
                        ? order.address.full_address
                        : order.address?.full_address ||
                          "Mannington,wv, 26582 Honduras"}
                    </span>
                  </div>
                </div>

                <div className="flex items-center">
                  <Clock className="h-4 w-4 text-gray-600 mr-2" />
                  <div className="flex-1 flex justify-between items-center">
                    <span className="text-sm font-medium text-gray-800">
                      Creado:
                    </span>
                    <span className="text-sm text-gray-800">
                      {isTestMode
                        ? order.delivery_time
                        : order.created_at
                          ? new Date(order.created_at).toLocaleString("es-SV", {
                              hour: "2-digit",
                              minute: "2-digit",
                              timeZone: "America/El_Salvador",
                            })
                          : "-"}
                    </span>
                  </div>
                </div>

                {order.comment && (
                  <div className="flex items-center">
                    <MessageSquare className="h-4 w-4 text-gray-600 mr-2" />
                    <div className="flex-1 flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-800">
                        Comentario:
                      </span>
                      <span className="text-sm text-gray-800 text-right max-w-[150px]">
                        {order.comment || "-"}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Resumen de pago */}
              <div className="mt-4 pt-4 border-t">
                <div className="space-y-3">
                  <div className="flex items-center">
                    <div className="flex-1 flex justify-between">
                      <span className="text-sm text-gray-800">
                        Medio de pago:
                      </span>
                      <span className="text-sm text-gray-800">
                        {order.paid_with === "cash" ? "Efectivo" : "Tarjeta"}
                      </span>
                    </div>
                  </div>

                  {[
                    {
                      label: "Subtotal",
                      value: order.subtotal,
                      valueClass: "text-gray-800",
                      prefix: "",
                    },
                    {
                      label: "Impuestos",
                      value: order.tax,
                      valueClass: "text-gray-800",
                      prefix: "",
                    },
                    {
                      label: "Costo de envío",
                      value: order.total_delivery_charge,
                      valueClass: "text-gray-800",
                      prefix: "",
                    },
                    {
                      label: "Descuento",
                      value: order.discount,
                      valueClass: "text-green-600",
                      prefix: "-",
                    },
                  ].map((item) => (
                    <div className="flex items-center" key={item.label}>
                      <div className="flex-1 flex justify-between">
                        <span className="text-sm text-gray-600">
                          {item.label}:
                        </span>
                        <span className={`text-sm ${item.valueClass}`}>
                          {formatCurrency(
                            item.prefix === "-"
                              ? -(item.value ?? 0)
                              : (item.value ?? 0),
                            order.currency,
                            { fractionDigits: 0 },
                          )}
                        </span>
                      </div>
                    </div>
                  ))}

                  <div className="flex items-center pt-2 border-t">
                    <div className="flex-1 flex justify-between">
                      <span className="text-sm font-medium text-gray-900">
                        Total:
                      </span>
                      <span className="text-sm font-medium text-gray-900">
                        {formatCurrency(
                          calculateOrderTotal(order),
                          order.currency,
                          { fractionDigits: 0 },
                        )}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Detalles del envío */}
              <div className="mt-4 pt-4 border-t">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-medium text-gray-900">
                    Detalles del envío
                  </h4>
                  <button className="text-xs text-blue-600 hover:underline">
                    Seguimiento
                  </button>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <Bike className="h-4 w-4 text-gray-600 mr-2" />
                    <div className="flex-1">
                      <div className="flex justify-between items-start">
                        <span className="text-sm font-medium text-gray-800">
                          Conductor:
                        </span>
                        <div className="flex flex-col items-end gap-1">
                          {(() => {
                            // Prefer server-provided driversHistory; fallback to last two local assignments
                            const history = Array.isArray(order.driversHistory)
                              ? order.driversHistory
                              : (order.driver_assignments || [])
                                  .slice(-2)
                                  .reverse();

                            // Test mode fallback if no history
                            if (
                              isTestMode &&
                              (!history || history.length === 0) &&
                              order.drivers
                            ) {
                              return (
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-900 font-medium">
                                    {order.drivers.name}
                                  </span>
                                  <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
                                    En camino
                                  </span>
                                </div>
                              );
                            }

                            if (!history || history.length === 0) {
                              return (
                                <div className="flex items-center gap-2">
                                  <span className="text-gray-400 text-sm">
                                    Sin asignar
                                  </span>
                                </div>
                              );
                            }

                            const current = history[0];
                            const previous = history[1];
                            return (
                              <div className="flex flex-col items-end gap-1">
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-900 font-medium">
                                    {current?.name || "Sin nombre"}
                                  </span>
                                  <span className="text-xs text-gray-400">
                                    (actual)
                                  </span>
                                  <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-800">
                                    {
                                      getOrderStatusDisplay(order.status)
                                        .display
                                    }
                                  </span>
                                </div>
                                {previous && (
                                  <div className="text-xs text-gray-500 italic">
                                    Anterior: {previous?.name || "Sin nombre"}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <Clock className="h-4 w-4 text-gray-600 mr-2" />
                    <div className="flex-1 flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-800">
                        Despachado:
                      </span>
                      <span className="text-sm text-gray-800">
                        {isTestMode
                          ? order.dispatch_time
                          : order.dispatch_date
                            ? new Date(order.dispatch_date).toLocaleTimeString(
                                "es-SV",
                                {
                                  hour: "2-digit",
                                  minute: "2-digit",
                                },
                              )
                            : "N/A"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  },
);

OrderItem.displayName = "OrderItem";

export default OrderSheet;
